import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { max, min } from 'd3-array';
import * as D3Drag from 'd3-drag';
import * as D3Selection from 'd3-selection';
import * as D3Array from 'd3-array';
import { getAllApplyingDatePresets } from '../../../../widgets/performanceOverview';
import { ChartSlider } from '../chartSlider';
import type { ChartSliderWrapperPropsType } from './types';

const getScaleSetFromRange = ( scaleSetArr, val: number ) => {
    return scaleSetArr.find( ( scaleSet ) => !!scaleSet.invert( val ) );
};

const ChartSliderWrapper: React.FC<ChartSliderWrapperPropsType> = ( props ) => {
    const getScaleSetFromDomain = useCallback( ( scaleSetArr, domainVal ) => {
        const filteredArr = scaleSetArr.filter( ( scaleSet ) => {
            return domainVal >= min( scaleSet.domain() ) &&
                domainVal <= max( scaleSet.domain() );
        } );

        return filteredArr[0] || null;
    }, [] );

    const getInitialSliderPositions = () => {
        if ( props.xScaleSets &&
            getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[0] ) &&
            getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[1] )
        ) {
            return {
                sliderLeftX: getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[0] )( props.detailXDomain[0] ),
                sliderRightX: getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[1] )( props.detailXDomain[1] ),
            };
        }
        return { sliderLeftX: 0, sliderRightX: 0 };
    };

    const initialPositions = getInitialSliderPositions();
    const [ sliderLeftX, setSliderLeftX ] = useState<number>( initialPositions.sliderLeftX );
    const [ sliderRightX, setSliderRightX ] = useState<number>( initialPositions.sliderRightX );

    const sliderLeftXRef = useRef( sliderLeftX );
    const sliderRightXRef = useRef( sliderRightX );
    const xRangeRef = useRef( props.xRange );
    const xScaleSetsRef = useRef( props.xScaleSets );
    const xDomainRef = useRef( props.xDomain );
    const updateHandlerRef = useRef( props.updateDetailXDomainHandler );
    const isDraggingRef = useRef( false );

    useEffect( () => {
        sliderLeftXRef.current = sliderLeftX;
    }, [ sliderLeftX ] );

    useEffect( () => {
        sliderRightXRef.current = sliderRightX;
    }, [ sliderRightX ] );

    useEffect( () => {
        xRangeRef.current = props.xRange;
    }, [ props.xRange ] );

    useEffect( () => {
        xScaleSetsRef.current = props.xScaleSets;
    }, [ props.xScaleSets ] );

    useEffect( () => {
        xDomainRef.current = props.xDomain;
    }, [ props.xDomain ] );

    useEffect( () => {
        updateHandlerRef.current = props.updateDetailXDomainHandler;
    }, [ props.updateDetailXDomainHandler ] );

    const restrictToBoundaries = useCallback( ( slider: number ) => {
        const xRange = xRangeRef.current;
        if ( slider < xRange[0] ) {
            return xRange[0];
        }
        else if ( slider > xRange[1] ) {
            return xRange[1];
        }
        else {
            return slider;
        }
    }, [] );

    useEffect( () => {
        if ( isDraggingRef.current ) {
            return;
        }
        if ( props.xScaleSets &&
            getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[0] ) &&
            getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[1] )
        ) {
            const newLeftX = getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[0] )( props.detailXDomain[0] );
            const newRightX = getScaleSetFromDomain( props.xScaleSets, props.detailXDomain[1] )( props.detailXDomain[1] );
            setSliderLeftX( newLeftX );
            setSliderRightX( newRightX );
            sliderLeftXRef.current = newLeftX;
            sliderRightXRef.current = newRightX;
        }
    }, [ props.xScaleSets, props.detailXDomain, getScaleSetFromDomain ] );

    useEffect( () => {
        const dragStarted = () => {
            isDraggingRef.current = true;
        };

        const dragEndedLeft = () => {
            const currentSliderLeftX = sliderLeftXRef.current;
            const currentSliderRightX = sliderRightXRef.current;
            const xScaleSets = xScaleSetsRef.current;
            const xDomain = xDomainRef.current;

            const detailXDomainLeft = new Date( getScaleSetFromRange(
                xScaleSets, currentSliderLeftX
            ).invert( currentSliderLeftX ).setHours( 0, 0, 0, 0 ) );

            const detailXDomainRight = new Date( getScaleSetFromRange(
                xScaleSets, currentSliderRightX
            ).invert( currentSliderRightX ).setHours( 0, 0, 0, 0 ) );

            const applyingDatePresets = getAllApplyingDatePresets( xDomain, detailXDomainLeft, detailXDomainRight );
            updateHandlerRef.current( [ detailXDomainLeft, detailXDomainRight ], applyingDatePresets, false );

            setTimeout( () => {
                isDraggingRef.current = false;
            }, 100 );
        };

        const dragEndedRight = () => {
            const currentSliderLeftX = sliderLeftXRef.current;
            const currentSliderRightX = sliderRightXRef.current;
            const xScaleSets = xScaleSetsRef.current;
            const xDomain = xDomainRef.current;

            const detailXDomainLeft = new Date( getScaleSetFromRange(
                xScaleSets, currentSliderLeftX
            ).invert( currentSliderLeftX ).setHours( 0, 0, 0, 0 ) );

            const detailXDomainRight = new Date( getScaleSetFromRange(
                xScaleSets, currentSliderRightX
            ).invert( currentSliderRightX ).setHours( 0, 0, 0, 0 ) );

            const applyingDatePresets = getAllApplyingDatePresets( xDomain, detailXDomainLeft, detailXDomainRight );
            updateHandlerRef.current( [ detailXDomainLeft, detailXDomainRight ], applyingDatePresets, false );

            setTimeout( () => {
                isDraggingRef.current = false;
            }, 100 );
        };

        const draggedLeft = ( event ) => {
            const x = event.x;
            const oppositeSideX = sliderRightXRef.current - 1;
            const newX = D3Array.min( [ restrictToBoundaries( x ), oppositeSideX ] );
            setSliderLeftX( newX );
            sliderLeftXRef.current = newX;
        };

        const draggedRight = ( event ) => {
            const x = event.x;
            const oppositeSideX = sliderLeftXRef.current + 1;
            const newX = D3Array.max( [ restrictToBoundaries( x ), oppositeSideX ] );
            setSliderRightX( newX );
            sliderRightXRef.current = newX;
        };

        const dragLeft = D3Drag.drag()
            .on( 'start', dragStarted )
            .on( 'drag', draggedLeft )
            .on( 'end', dragEndedLeft );

        const dragRight = D3Drag.drag()
            .on( 'start', dragStarted )
            .on( 'drag', draggedRight )
            .on( 'end', dragEndedRight );

        D3Selection.select( '.slider-left' ).call( dragLeft );
        D3Selection.select( '.slider-right' ).call( dragRight );
    }, [ restrictToBoundaries ] );

    const getGrayedOverlayDimensions = useCallback( () => {
        const boundaryWidth = Math.max( ...props.xRange );
        const boundaryHeight = Math.max( ...props.yRange );
        return {
            gap: {
                x: -10,
                y: 0,
                width: 10,
                height: boundaryHeight,
            },
            left: {
                x: 0,
                y: 0,
                width: sliderLeftX,
                height: boundaryHeight,
            },
            right: {
                x: sliderRightX,
                y: 0,
                width: boundaryWidth - sliderRightX,
                height: boundaryHeight,
            },
        };
    }, [ props.xRange, props.yRange, sliderLeftX, sliderRightX ] );

    const {
        yRange,
        xRange,
        xScaleSets,
        timeFormatter,
    } = props;

    const labelsTooClose = ( sliderRightX - sliderLeftX < 125 );
    const grayedOverlayDimensions = getGrayedOverlayDimensions();

    return (
        <g className='sliderWrapper'>
            <rect className='sliderGrayedRectGap fill-[#f3f3f3]' { ...grayedOverlayDimensions.gap } />

            <g>
                <rect className='sliderGrayedRectBlendLighten fill-[#d8d8d8]' style={ { mixBlendMode: 'lighten' } } { ...grayedOverlayDimensions.left } />
                <rect className='sliderGrayedRectBlendGrayscale fill-transparent' style={ { mixBlendMode: 'saturation' } } { ...grayedOverlayDimensions.left } />
            </g>

            <g>
                <rect className='sliderGrayedRectBlendLighten fill-[#d8d8d8]' style={ { mixBlendMode: 'lighten' } } { ...grayedOverlayDimensions.right } />
                <rect className='sliderGrayedRectBlendGrayscale fill-transparent' style={ { mixBlendMode: 'saturation' } } { ...grayedOverlayDimensions.right } />
            </g>

            <rect className='sliderGrayedRect fill-black opacity-[0.047058824]' { ...grayedOverlayDimensions.left } />
            <rect className='sliderGrayedRect fill-black opacity-[0.047058824]' { ...grayedOverlayDimensions.right } />
            <ChartSlider
                xPos={ sliderLeftX }
                type='left'
                labelsTooClose={ labelsTooClose }
                xScaleSets={ xScaleSets }
                xRange={ xRange }
                yRange={ yRange }
                timeFormatter={ timeFormatter }
            />
            <ChartSlider
                xPos={ sliderRightX }
                type='right'
                labelsTooClose={ labelsTooClose }
                xScaleSets={ xScaleSets }
                xRange={ xRange }
                yRange={ yRange }
                timeFormatter={ timeFormatter }
            />
        </g>
    );
};

export { ChartSliderWrapper };
